import { userDetailsApi as api } from './userDetailsApi';
export const addTagTypes = ['HelpQas', 'ContactUs', 'UserDetails'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      getHelpQas: build.query<GetHelpQasApiResponse, GetHelpQasApiArg>({
        query: queryArg => ({
          url: `/helpQas`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId
          }
        }),
        providesTags: ['HelpQas']
      }),
      getContactUs: build.query<GetContactUsApiResponse, GetContactUsApiArg>({
        query: queryArg => ({
          url: `/contactUs`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId
          }
        }),
        providesTags: ['ContactUs']
      }),
      addContactUs: build.mutation<AddContactUsApiResponse, AddContactUsApiArg>(
        {
          query: queryArg => ({
            url: `/contactUs`,
            method: 'POST',
            body: queryArg.createContactUs
          }),
          invalidatesTags: ['ContactUs']
        }
      ),
      userDetailsByUserId: build.query<
        UserDetailsByUserIdApiResponse,
        UserDetailsByUserIdApiArg
      >({
        query: queryArg => ({
          url: `/userDetails`,
          params: {
            userId: queryArg.userId
          }
        }),
        providesTags: ['UserDetails']
      }),
      addUserDetails: build.mutation<
        AddUserDetailsApiResponse,
        AddUserDetailsApiArg
      >({
        query: queryArg => ({
          url: `/userDetails`,
          method: 'POST',
          body: queryArg.createUserDetails
        }),
        invalidatesTags: ['UserDetails']
      }),
      getUserDetailsById: build.query<
        GetUserDetailsByIdApiResponse,
        GetUserDetailsByIdApiArg
      >({
        query: queryArg => ({ url: `/userDetails/${queryArg.id}` }),
        providesTags: ['UserDetails']
      }),
      updateUserDetails: build.mutation<
        UpdateUserDetailsApiResponse,
        UpdateUserDetailsApiArg
      >({
        query: queryArg => ({
          url: `/userDetails/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateUserDetails
        }),
        invalidatesTags: ['UserDetails']
      }),
      deleteUserDetails: build.mutation<
        DeleteUserDetailsApiResponse,
        DeleteUserDetailsApiArg
      >({
        query: queryArg => ({
          url: `/userDetails/${queryArg.id}`,
          method: 'DELETE',
          params: { delete: queryArg['delete'] }
        }),
        invalidatesTags: ['UserDetails']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type GetHelpQasApiResponse = /** status 200 HelpQas found. */ {
  ok?: boolean;
  data?: HelpQas;
  message?: string;
};
export type GetHelpQasApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type GetContactUsApiResponse = /** status 200 Contactuses found. */ {
  ok?: boolean;
  data?: ContactUs;
  message?: string;
};
export type GetContactUsApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type AddContactUsApiResponse =
  /** status 201 ContactUs has been created successfully. */ {
    ok?: boolean;
    data?: ContactUs;
    message?: string;
  };
export type AddContactUsApiArg = {
  /** ContactUs that needs to be added to the application */
  createContactUs: CreateContactUs;
};
export type UserDetailsByUserIdApiResponse =
  /** status 200 UserDetails details found. */ {
    ok?: boolean;
    data?: UserDetails;
    message?: string;
  };
export type UserDetailsByUserIdApiArg = {
  /** ID of the UserDetails to retrieve */
  userId: string;
};
export type AddUserDetailsApiResponse =
  /** status 201 UserDetails has been created successfully. */ {
    ok?: boolean;
    data?: UserDetails;
    message?: string;
  };
export type AddUserDetailsApiArg = {
  /** UserDetails that needs to be added to the application */
  createUserDetails: CreateUserDetails;
};
export type GetUserDetailsByIdApiResponse =
  /** status 200 UserDetails details found. */ {
    ok?: boolean;
    data?: UserDetails;
    message?: string;
  };
export type GetUserDetailsByIdApiArg = {
  /** ID of the UserDetails to retrieve */
  id: string;
};
export type UpdateUserDetailsApiResponse =
  /** status 200 The UserDetails's details has been updated successfully */ {
    ok?: boolean;
    data?: UserDetails;
    message?: string;
  };
export type UpdateUserDetailsApiArg = {
  /** ID of the UserDetails to update */
  id: string;
  /** UserDetails that needs to be added to the application */
  updateUserDetails: UpdateUserDetails;
};
export type DeleteUserDetailsApiResponse =
  /** status 200 The UserDetails's details has been deleted successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type DeleteUserDetailsApiArg = {
  /** ID of the UserDetails to delete */
  id: string;
  /** flag to hard delete */
  delete?: string;
};
export type HelpQas = {
  count?: number;
  rows?: {
    id: string;
    questions: string;
    answers: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
  }[];
};
export type ContactUs = {
  id?: string;
  name?: string;
  email?: string;
  contactNumber?: string;
  subject?: string;
  details?: string;
  url?: string;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  contactUsAssets?: {
    url?: string;
  }[];
};
export type CreateContactUs = {
  name: string;
  email: string;
  contactNumber: string;
  subject: string;
  details: string;
  url: string[];
};
export type UserDetails = {
  id: string;
  userId: string;
  profileImage: string;
  stripeCustomerId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  email: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  type: string;
  contactNumber: string;
};
export type CreateUserDetails = {
  firstName?: string;
  lastName?: string;
  profileImage?: string;
  stripeCustomerId?: string;
  dateOfBirth?: string;
  email?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  type?: string;
  contactNumber?: string;
};
export type UpdateUserDetails = {
  firstName: string;
  lastName: string;
  profileImage: string;
  stripeCustomerId: string;
  dateOfBirth: string;
  email: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  type: string;
  contactNumber: string;
};
export const {
  useGetContactUsQuery,
  useAddContactUsMutation,
  useGetHelpQasQuery,
  useUserDetailsByUserIdQuery,
  useAddUserDetailsMutation,
  useGetUserDetailsByIdQuery,
  useUpdateUserDetailsMutation,
  useDeleteUserDetailsMutation
} = injectedRtkApi;
